import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

const Purchase = () => {
  const { user, updateUser, refreshUser } = useAuth();
  const { success, error } = useNotification();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [purchasedKey, setPurchasedKey] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products');
      setProducts(response.data.filter(p => p.isActive));
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    setPurchasing(true);
    
    try {
      const response = await axios.post('/api/keys/purchase', {
        productId: selectedProduct,
        duration: selectedDuration
      });
      
      // Update user credits in context
      const updatedUser = { 
        ...user, 
        credits: response.data.remainingCredits 
      };
      updateUser(updatedUser);
      
      setPurchasedKey(response.data.licenseKey);
      success(`License key purchased successfully!\nRemaining credits: ${response.data.remainingCredits}`, 8000);
      
      // Trigger event to notify MyKeys component
      window.dispatchEvent(new CustomEvent('keyPurchased', { 
        detail: response.data.licenseKey 
      }));
      
      // Refresh products to get updated stats
      fetchProducts();
      
      // Reset form
      setSelectedProduct('');
      setSelectedDuration(1);
    } catch (err) {
      error(err.response?.data?.message || 'Failed to purchase license key');
    } finally {
      setPurchasing(false);
    }
  };

  const handleRefreshBalance = async () => {
    try {
      const response = await axios.get('/api/users/profile');
      updateUser(response.data);
      success('Balance refreshed!', 2000);
    } catch (err) {
      error('Failed to refresh balance');
    }
  };


  const calculateCost = (product, days) => {
    if (!product) return 0;
    return product.baseCreditCost + (days * product.creditsPerDay);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    success('License key copied to clipboard!', 2000);
  };

  const selectedProductData = products.find(p => p._id === selectedProduct);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-astral p-6">
        <h1 className="text-3xl font-bold text-white mb-2">Purchase License Keys</h1>
        <p className="text-gray-400">
          Purchase license keys for any available product using your credits.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Purchase Form */}
        <div className="card-astral p-6">
          <h2 className="text-xl font-bold text-white mb-4">Purchase Form</h2>
          
          {/* Balance Display */}
          <div className="bg-dark-200 p-4 rounded-lg space-y-2 mb-6">
            <div className="flex justify-between items-center">
              <h4 className="text-white font-semibold">Your Balance</h4>
              <button
                onClick={handleRefreshBalance}
                className="text-blue-400 hover:text-blue-300 text-sm"
                title="Refresh balance"
              >
                🔄
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-2 bg-green-600/10 rounded">
                <div className="text-green-400 font-semibold">${user?.balance?.toFixed(2) || '0.00'}</div>
                <div className="text-xs text-gray-400">USD Balance</div>
              </div>
              <div className="text-center p-2 bg-blue-600/10 rounded">
                <div className="text-blue-400 font-semibold">{user?.credits || 0}</div>
                <div className="text-xs text-gray-400">Credits</div>
              </div>
            </div>
          </div>
          
          <form onSubmit={handlePurchase} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Select Product
              </label>
              <select
                value={selectedProduct}
                onChange={(e) => {
                  setSelectedProduct(e.target.value);
                  const product = products.find(p => p._id === e.target.value);
                  if (product) {
                    setSelectedDuration(product.minDays || 1);
                  }
                }}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                required
              >
                <option value="">Choose a product...</option>
                {products.map((product) => (
                  <option key={product._id} value={product._id}>
                    {product.name} - {product.baseCreditCost} + {product.creditsPerDay}/day credits
                  </option>
                ))}
              </select>
            </div>

            {selectedProductData && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  License Duration ({selectedProductData.minDays}-{selectedProductData.maxDays} days)
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min={selectedProductData.minDays}
                    max={selectedProductData.maxDays}
                    value={selectedDuration}
                    onChange={(e) => setSelectedDuration(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>{selectedProductData.minDays} days</span>
                    <span className="text-white font-semibold">{selectedDuration} days</span>
                    <span>{selectedProductData.maxDays} days</span>
                  </div>
                </div>
              </div>
            )}

            {selectedProductData && (
              <div className="bg-dark-200 p-4 rounded-lg space-y-2">
                <h4 className="text-white font-semibold mb-2">Purchase Summary</h4>
                <div className="flex justify-between">
                  <span className="text-gray-400">Product:</span>
                  <span className="text-white">{selectedProductData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Duration:</span>
                  <span className="text-white">{selectedDuration} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Base cost:</span>
                  <span className="text-blue-400">{selectedProductData.baseCreditCost} credits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Duration cost:</span>
                  <span className="text-green-400">{selectedDuration} × {selectedProductData.creditsPerDay} = {selectedDuration * selectedProductData.creditsPerDay} credits</span>
                </div>
                <div className="flex justify-between border-t border-gray-600 pt-2">
                  <span className="text-gray-400 font-semibold">Total cost:</span>
                  <span className="text-purple-400 font-semibold">{calculateCost(selectedProductData, selectedDuration)} credits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Your credits:</span>
                  <span className="text-white">{user?.credits || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">After purchase:</span>
                  <span className={`font-semibold ${
                    (user?.credits || 0) >= calculateCost(selectedProductData, selectedDuration)
                      ? 'text-green-400'
                      : 'text-red-400'
                  }`}>
                    {(user?.credits || 0) - calculateCost(selectedProductData, selectedDuration)} credits
                  </span>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={!selectedProduct || purchasing || 
                       (selectedProductData && (user?.credits || 0) < calculateCost(selectedProductData, selectedDuration))}
              className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${
                selectedProduct && selectedProductData && 
                (user?.credits || 0) >= calculateCost(selectedProductData, selectedDuration) && !purchasing
                  ? 'btn-neon' 
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {purchasing ? (
                <div className="flex items-center justify-center">
                  <div className="spinner mr-2"></div>
                  Processing Purchase...
                </div>
              ) : !selectedProduct ? (
                'Select Product to Purchase'
              ) : selectedProductData && (user?.credits || 0) < calculateCost(selectedProductData, selectedDuration) ? (
                'Insufficient Credits'
              ) : (
                `Purchase for ${calculateCost(selectedProductData, selectedDuration)} credits`
              )}
            </button>
          </form>
        </div>

        {/* Available Products */}
        <div className="card-astral p-6">
          <h2 className="text-xl font-bold text-white mb-4">Available Products</h2>
          
          {products.length === 0 ? (
            <p className="text-gray-400">No products available for purchase.</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {products.map((product) => (
                <div key={product._id} className="bg-dark-200 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white">{product.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-1 bg-purple-600/20 text-purple-400 text-xs rounded font-mono">
                          {product.keyTag}
                        </span>
                        <span className="px-2 py-1 bg-blue-600/20 text-blue-400 text-xs rounded capitalize">
                          {product.productType}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-green-400 font-semibold text-sm">
                        {product.baseCreditCost} + {product.creditsPerDay}/day
                      </div>
                      <div className="text-xs text-gray-400">credits</div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">{product.description}</p>
                  
                  <div className="mt-2 p-2 bg-dark-100 rounded">
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-400">1 day:</span>
                        <span className="text-white">{product.baseCreditCost + product.creditsPerDay} credits</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">7 days:</span>
                        <span className="text-white">{product.baseCreditCost + (7 * product.creditsPerDay)} credits</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">30 days:</span>
                        <span className="text-white">{product.baseCreditCost + (30 * product.creditsPerDay)} credits</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2 text-xs text-gray-500">
                    Duration: {product.minDays}-{product.maxDays} days
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Purchased Key Display */}
      {purchasedKey && (
        <div className="card-astral p-6">
          <h2 className="text-xl font-bold text-white mb-4">Purchase Successful! 🎉</h2>
          
          <div className="bg-green-600/20 border border-green-500/50 rounded-lg p-4">
            <h3 className="text-green-400 font-semibold mb-3">Your License Key</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Product:</span>
                <span className="text-white">{purchasedKey.product}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Duration:</span>
                <span className="text-white">{purchasedKey.duration} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Expires:</span>
                <span className="text-white">{new Date(purchasedKey.expiryDate).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Cost:</span>
                <span className="text-green-400">{purchasedKey.cost} credits</span>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-dark-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-gray-400 text-sm block">License Key:</span>
                  <code className="text-purple-400 font-mono text-lg">{purchasedKey.key}</code>
                </div>
                <button
                  onClick={() => copyToClipboard(purchasedKey.key)}
                  className="text-gray-400 hover:text-purple-400 transition-colors ml-4"
                  title="Copy to clipboard"
                >
                  📋
                </button>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-600/20 border border-blue-500/50 rounded">
              <p className="text-blue-400 text-sm">
                💡 Save this license key in a secure location. You can also view it later in "My Keys" section.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Purchase;
